import React, { useState, useEffect, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';
import { isAoAdmin } from './AoSociete';
import { useConfirm } from './ConfirmDialog';

/**
 * Diagnostic « Score local vs score IA » — réservé aux administrateurs.
 *
 * Outil de DÉCISION, pas d'affichage métier : il sert à trancher si le moteur
 * local (déterministe, gratuit, ~10 ms) peut remplacer ou pré-filtrer le
 * scoring IA. Le score local n'apparaît donc NULLE PART ailleurs : côté
 * recruteur, un seul score fait foi — celui de l'IA.
 *
 * Ce que l'écran met en avant, dans cet ordre : la corrélation de RANG (deux
 * moteurs peuvent diverger de 15 points et classer pareil — c'est le
 * classement qui décide), puis le podium, puis l'accord à ±10 points. Les
 * écarts de points restent secondaires, jamais le titre.
 *
 * GET /matching/comparaison?besoin_id=            → le lot
 * GET /matching/comparaison?besoin_id=&candidate_id=  → une paire détaillée
 * Le paramètre scorer_ia=1 déclenche des appels IA PAYANTS : il n'est jamais
 * envoyé au chargement, uniquement derrière un bouton et une confirmation.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
};
const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const LBL = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' };
const CARTE = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '11px', padding: '13px 15px' };

// Un nombre, ou null quand la valeur n'est pas exploitable : on n'affiche
// jamais 0 à la place d'une absence de mesure.
const nb = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const arrondi = (v, d = 2) => (nb(v) == null ? null : Number(v).toFixed(d));
const nomDe = (c) => {
  if (c == null) return '—';
  if (typeof c === 'string') return c;
  return [c.prenom, c.nom].filter(Boolean).join(' ').trim() || c.nom || c.libelle || `Candidat ${c.id ?? ''}`.trim() || '—';
};
const idDe = (l) => l?.candidate_id ?? l?.candidat_id ?? l?.candidat?.id ?? l?.candidat?.candidate_id ?? null;

// Lecture tolérante : le serveur peut nicher le score ou l'exposer à plat.
const scoreDe = (bloc) => (bloc == null ? null : nb(typeof bloc === 'number' ? bloc : bloc.score));

// Sources : dès qu'un côté est calculé localement, la comparaison porte sur un
// barème indicatif — il faut le dire, sinon on lit un accord qui n'en est pas un.
const baremeIndicatif = (s) => {
  const crit = String(s?.criteres || '');
  const cv = String(s?.cv || '');
  return crit === 'local' || cv === 'parse_local';
};

const Chip = ({ ton = 'mut', children, title }) => {
  const T = {
    ok: { bg: 'rgba(22,163,74,0.13)', fg: '#16a34a' },
    warn: { bg: 'rgba(217,119,6,0.13)', fg: '#b45309' },
    bad: { bg: 'rgba(207,59,50,0.13)', fg: '#cf3b32' },
    mut: { bg: 'var(--chip-bg)', fg: 'var(--text-2)' },
  }[ton] || {};
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: T.bg, color: T.fg, whiteSpace: 'nowrap' }}>{children}</span>
  );
};

/** Corrélation de rang : l'indicateur qui tranche. */
const tonCorrelation = (c) => (c == null ? 'mut' : c >= 0.8 ? 'ok' : c >= 0.5 ? 'warn' : 'bad');
const motCorrelation = (c) => {
  if (c == null) return 'non mesurable';
  if (c >= 0.8) return 'classements très proches';
  if (c >= 0.5) return 'classements partiellement d\'accord';
  return 'classements divergents';
};

/**
 * L'écran de diagnostic, en modale plein cadre. `besoinId` est requis ;
 * `besoinTitre` sert seulement d'en-tête tant que la réponse n'est pas là.
 */
export function ComparaisonScoresModal({ besoinId, besoinTitre, showNotification, onClose }) {
  const { confirm, confirmElement } = useConfirm();
  const [data, setData] = useState(null);      // réponse « lot »
  const [etat, setEtat] = useState('chargement'); // chargement | ok | interdit | absent | erreur
  const [erreur, setErreur] = useState(null);
  const [paire, setPaire] = useState(null);    // { chargement, data, candidatNom } | null
  const [ia, setIa] = useState(false);         // calcul IA payant en cours

  // Un chargement d'écran = un appel. scorer_ia n'est JAMAIS passé ici.
  const charger = useCallback(async (opts) => {
    const avecIa = opts?.scorerIa === true;
    if (avecIa) setIa(true); else setEtat('chargement');
    try {
      const url = `${BASE}/matching/comparaison?besoin_id=${encodeURIComponent(besoinId)}${avecIa ? '&scorer_ia=1' : ''}`;
      const r = await fetch(url, { headers: authH() });
      if (r.status === 403) { setEtat('interdit'); return; }
      if (r.status === 404 || r.status === 501) { setEtat('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      setData(j.data || j);
      setEtat('ok');
    } catch (e) { setErreur(e.message); setEtat('erreur'); }
    finally { if (avecIa) setIa(false); }
  }, [besoinId]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirPaire = useCallback(async (ligne) => {
    const cid = idDe(ligne);
    if (cid == null) return;
    setPaire({ chargement: true, data: null, candidatNom: nomDe(ligne.candidat) });
    try {
      const r = await fetch(`${BASE}/matching/comparaison?besoin_id=${encodeURIComponent(besoinId)}&candidate_id=${encodeURIComponent(cid)}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      setPaire({ chargement: false, data: j.data || j, candidatNom: nomDe(ligne.candidat) });
    } catch (e) {
      setPaire(null);
      showNotification?.(`Détail indisponible : ${e.message}`, 'error');
    }
  }, [besoinId, showNotification]);

  // Appels IA payants : jamais sans un oui explicite.
  const calculerIa = async () => {
    const manquants = nb(data?.synthese?.sans_score_ia) ?? 0;
    const ok = await confirm({
      title: 'Calculer les scores IA manquants',
      message: `Lancer le scoring IA pour ${manquants || 'les'} candidat${manquants > 1 ? 's' : ''} sans score en cache ?`,
      detail: 'Ces calculs sont facturés à l\'usage. Les candidats déjà scorés ne sont pas recalculés.',
      confirmLabel: 'Calculer (payant)',
      tone: 'primary',
    });
    if (ok) charger({ scorerIa: true });
  };

  const s = data?.synthese || {};
  const lignes = Array.isArray(data?.lignes) ? data.lignes : [];
  const correlation = nb(s.correlation_rang);
  const memePodium = s.podium?.memes_candidats === true;
  const titre = data?.besoin?.titre || data?.besoin?.poste || besoinTitre || '';

  const carteIndic = (libelle, valeur, sous) => (
    <div style={{ ...CARTE, padding: '11px 13px', minWidth: 0 }}>
      <div style={LBL}>{libelle}</div>
      <div style={{ ...mono, marginTop: '4px', fontSize: '17px', fontWeight: 600, color: 'var(--text-strong)' }}>{valeur ?? '—'}</div>
      {sous && <div style={{ marginTop: '2px', fontSize: '11px', color: 'var(--text-3)' }}>{sous}</div>}
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 620, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {confirmElement}
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(1080px, 97vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', borderBottom: '1px solid var(--hair)', flex: 'none' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: '17px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>
              {paire ? `Détail — ${paire.candidatNom}` : 'Score local ou score IA : que dit la comparaison ?'}
            </div>
            {titre && <div style={{ marginTop: '2px', fontSize: '12.5px', color: 'var(--text-3)' }}>{titre}</div>}
          </div>
          {paire && <button onClick={() => setPaire(null)} style={btnGhost}><BI icon="chevron-left" /> Retour au lot</button>}
          <button onClick={onClose} style={{ ...btnGhost, width: '32px', padding: 0, justifyContent: 'center' }} title="Fermer"><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 18px' }}>
          {etat === 'chargement' && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
              <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'mc-spin .8s linear infinite', marginRight: '7px' }} /> Comparaison en cours…
              <style>{'@keyframes mc-spin{to{transform:rotate(360deg)}}'}</style>
            </div>
          )}
          {etat === 'interdit' && <Message icone="lock" titre="Réservé à l'administration" texte="Ce diagnostic n'est accessible qu'aux administrateurs de l'organisation." />}
          {etat === 'absent' && <Message icone="hourglass-split" titre="Diagnostic indisponible" texte="La comparaison des moteurs de score arrive côté serveur." />}
          {etat === 'erreur' && <Message icone="exclamation-triangle" titre="Comparaison impossible" texte={erreur} />}

          {etat === 'ok' && !paire && (
            <>
              {/* Conclusion prête à lire, telle que le serveur la formule. */}
              {data?.lecture && (
                <div style={{ ...CARTE, background: 'var(--ghost-bg)', marginBottom: '14px', fontSize: '13.5px', lineHeight: 1.6, color: 'var(--text)' }}>
                  {data.lecture}
                </div>
              )}

              {/* L'indicateur de décision d'abord, en grand : c'est le CLASSEMENT
                  qui tranche, pas l'écart de points. */}
              <div style={{ ...CARTE, display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div style={{ minWidth: '170px' }}>
                  <div style={LBL}>Accord de classement</div>
                  <div style={{ ...mono, marginTop: '2px', fontSize: '34px', fontWeight: 600, lineHeight: 1.1, color: 'var(--text-strong)' }}>
                    {arrondi(correlation) ?? '—'}
                  </div>
                  <div style={{ marginTop: '3px' }}><Chip ton={tonCorrelation(correlation)}>{motCorrelation(correlation)}</Chip></div>
                </div>
                <div style={{ flex: 1, minWidth: '230px', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                  Corrélation de rang entre les deux moteurs (de -1 à 1). C'est elle qui décide :
                  deux moteurs peuvent noter à 15 points d'écart et pourtant ranger les candidats
                  dans le même ordre — auquel cas le moteur local suffit pour trier ou pré-filtrer.
                </div>
                <div style={{ flex: 'none' }}>
                  {s.podium
                    ? <Chip ton={memePodium ? 'ok' : 'warn'}>
                        <BI icon={memePodium ? 'check-circle-fill' : 'exclamation-circle'} /> {memePodium ? 'Même top 3' : 'Top 3 différent'}
                      </Chip>
                    : null}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(158px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {carteIndic('Accord à ±10 points', nb(s.accord_a_10_points) != null ? `${arrondi(s.accord_a_10_points, 0)} %` : null, 'candidats notés au même niveau')}
                {carteIndic('Comparables', nb(s.comparables), 'paires avec les deux scores')}
                {carteIndic('Sans score IA', nb(s.sans_score_ia), 'hors comparaison')}
                {carteIndic('Écart moyen', arrondi(s.ecart_moyen, 1), nb(s.ecart_max) != null ? `max ${arrondi(s.ecart_max, 1)}` : null)}
                {carteIndic('Calcul local', nb(s.duree_locale_moyenne_ms) != null ? `${arrondi(s.duree_locale_moyenne_ms, 0)} ms` : null, 'par candidat, en moyenne')}
              </div>

              {nb(s.sans_score_ia) > 0 && (
                <div style={{ ...CARTE, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px', borderColor: 'rgba(217,119,6,0.35)', background: 'rgba(217,119,6,0.06)' }}>
                  <BI icon="exclamation-triangle" style={{ color: '#b45309', flex: 'none' }} />
                  <div style={{ flex: 1, minWidth: '220px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.55 }}>
                    {nb(s.sans_score_ia)} candidat{nb(s.sans_score_ia) > 1 ? 's n\'ont' : ' n\'a'} pas de score IA en cache : {nb(s.sans_score_ia) > 1 ? 'ils sont exclus' : 'il est exclu'} de la comparaison.
                  </div>
                  <button onClick={calculerIa} disabled={ia} style={{ ...btnGhost, opacity: ia ? 0.6 : 1 }}>
                    <BI icon={ia ? 'arrow-repeat' : 'stars'} style={ia ? { animation: 'mc-spin .8s linear infinite' } : undefined} />
                    {ia ? 'Calcul en cours…' : 'Calculer les scores IA manquants (payant)'}
                  </button>
                </div>
              )}

              <TableauLot lignes={lignes} onLigne={ouvrirPaire} />

              {data?.local_version && (
                <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-faint)' }}>
                  Moteur local : version {String(data.local_version)}.
                </div>
              )}
            </>
          )}

          {paire?.chargement && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
              <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'mc-spin .8s linear infinite', marginRight: '7px' }} /> Lecture du détail…
            </div>
          )}
          {paire && !paire.chargement && <VuePaire d={paire.data} />}
        </div>
      </div>
    </div>
  );
}

const Message = ({ icone, titre, texte }) => (
  <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-2)' }}>
    <BI icon={icone} style={{ fontSize: '24px', color: 'var(--text-3)' }} />
    <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)' }}>{titre}</div>
    {texte && <div style={{ marginTop: '5px', fontSize: '12.5px', color: 'var(--text-3)' }}>{texte}</div>}
  </div>
);

/** Une ligne par candidat : local, IA, écart signé, provenance des données. */
function TableauLot({ lignes, onLigne }) {
  if (!lignes.length) return <Message icone="people" titre="Aucun candidat à comparer" texte="Ce besoin n'a pas encore de candidat positionné." />;
  const th = { ...LBL, textAlign: 'left', padding: '0 10px 7px', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', borderTop: '1px solid var(--hair)', fontSize: '13px', color: 'var(--text)', verticalAlign: 'middle' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
        <thead>
          <tr>
            <th style={th}>Candidat</th>
            <th style={{ ...th, textAlign: 'right' }}>Local</th>
            <th style={{ ...th, textAlign: 'right' }}>IA</th>
            <th style={{ ...th, textAlign: 'right' }}>Écart</th>
            <th style={th}>Données comparées</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => {
            const local = scoreDe(l.local);
            const iaScore = scoreDe(l.ia);
            const ecart = nb(l.ecart);
            const raison = l.ia_raison || l.ia?.raison || null;
            const avert = Array.isArray(l.avertissements) ? l.avertissements.filter(Boolean) : [];
            const indicatif = baremeIndicatif(l.sources);
            return (
              <tr key={idDe(l) ?? i} onClick={() => onLigne(l)} className="src-row-hover" style={{ cursor: 'pointer' }}>
                <td style={{ ...td, fontWeight: 600, color: 'var(--text-strong)' }}>
                  {nomDe(l.candidat)}
                  {avert.length > 0 && (
                    <BI icon="exclamation-triangle" style={{ marginLeft: '7px', color: '#b45309', fontSize: '12px' }} title={avert.join('\n')} />
                  )}
                </td>
                <td style={{ ...td, ...mono, textAlign: 'right' }}>{local ?? '—'}</td>
                <td style={{ ...td, ...mono, textAlign: 'right' }}>
                  {iaScore != null
                    ? iaScore
                    : <span title={raison || undefined} style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '11.5px', color: 'var(--text-faint)' }}>non scoré par l'IA</span>}
                </td>
                <td style={{ ...td, ...mono, textAlign: 'right', fontWeight: 600, color: ecart == null ? 'var(--text-3)' : Math.abs(ecart) <= 10 ? 'var(--text-2)' : ecart > 0 ? '#16a34a' : '#cf3b32' }}>
                  {ecart == null ? '' : `${ecart > 0 ? '+' : ''}${arrondi(ecart, 1)}`}
                </td>
                <td style={td}>
                  <span style={{ display: 'inline-flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {l.sources?.criteres && <Chip title="Provenance des critères">{String(l.sources.criteres) === 'ia_cache' ? 'critères IA' : 'critères locaux'}</Chip>}
                    {l.sources?.cv && <Chip title="Provenance de la lecture du CV">{{ structure_ia_cache: 'CV lu par IA', parse_local: 'CV lu localement', absent: 'CV absent' }[String(l.sources.cv)] || String(l.sources.cv)}</Chip>}
                    {indicatif && <Chip ton="warn" title="Une partie de la comparaison s'appuie sur le barème local : l'accord observé est moins probant.">barème indicatif</Chip>}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Vue paire : critère par critère, les deux preuves côte à côte. */
function VuePaire({ d }) {
  if (!d) return null;
  const criteres = Array.isArray(d.criteres) ? d.criteres : null;
  const ligne = Array.isArray(d.lignes) ? d.lignes[0] : null;
  const det = ligne?.detail_local || d.detail_local || null;
  const th = { ...LBL, textAlign: 'left', padding: '0 10px 7px' };
  const td = { padding: '10px', borderTop: '1px solid var(--hair)', fontSize: '12.5px', color: 'var(--text)', verticalAlign: 'top' };
  // E null + statut 'a_verifier' = « pas évaluable », surtout pas 0.
  const cellE = (E, statut) => (statut === 'a_verifier' || (E == null && statut))
    ? <span style={{ fontSize: '11.5px', color: '#b45309', fontWeight: 600 }}>à vérifier</span>
    : <span style={{ ...mono, fontWeight: 600 }}>{nb(E) ?? '—'}</span>;

  return (
    <>
      {det && (
        <div style={{ ...CARTE, marginBottom: '14px' }}>
          <div style={{ ...LBL, marginBottom: '9px' }}>Ce qu'a vu le moteur local</div>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Score</div><div style={{ ...mono, fontSize: '19px', fontWeight: 600, color: 'var(--text-strong)' }}>{nb(det.score) ?? '—'}</div></div>
            {det.verdict && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Verdict</div><div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{String(det.verdict)}</div></div>}
            {det.role_fit != null && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Adéquation au rôle</div><div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{String(det.role_fit)}</div></div>}
            {det.risque != null && <div><div style={{ fontSize: '11px', color: 'var(--text-3)' }}>Risque</div><div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{String(det.risque)}</div></div>}
          </div>
          {det.role_fit_reason && <div style={{ marginTop: '9px', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.55 }}>{det.role_fit_reason}</div>}
          {/* Les plafonds expliquent un score bridé : les cacher rendrait le
              score local incompréhensible. */}
          {Array.isArray(det.caps) && det.caps.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>Plafonds appliqués</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {det.caps.map((c, i) => <Chip key={i} ton="warn">{typeof c === 'string' ? c : (c.libelle || c.label || c.motif || JSON.stringify(c))}</Chip>)}
              </div>
            </div>
          )}
          {Array.isArray(det.flags) && det.flags.length > 0 && (
            <div style={{ marginTop: '9px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {det.flags.map((f, i) => <Chip key={i}>{typeof f === 'string' ? f : (f.libelle || f.label || JSON.stringify(f))}</Chip>)}
            </div>
          )}
        </div>
      )}

      {criteres === null ? (
        <Message icone="info-circle" titre="Pas de détail IA pour cette paire"
          texte="Aucune évaluation IA détaillée n'est en cache pour ce candidat sur ce besoin — seul le détail local ci-dessus est disponible." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* L'ordre vient du serveur (écart décroissant) : c'est le plus
              instructif, on ne le retrie pas. */}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
            <thead>
              <tr>
                <th style={th}>Critère</th>
                <th style={{ ...th, width: '38%' }}>Moteur local</th>
                <th style={{ ...th, width: '38%' }}>Moteur IA</th>
                <th style={{ ...th, textAlign: 'right' }}>Écart</th>
              </tr>
            </thead>
            <tbody>
              {criteres.map((c, i) => {
                const eLocal = c.local?.E ?? c.E_local ?? null;
                const eIa = c.ia?.E ?? c.E_ia ?? null;
                const pLocal = c.local?.preuve ?? c.preuve_local ?? null;
                const pIa = c.ia?.preuve ?? c.preuve_ia ?? null;
                const stLocal = c.local?.statut ?? c.statut ?? null;
                const stIa = c.ia?.statut ?? null;
                const ecartE = nb(c.ecart_E ?? c.ecart);
                return (
                  <tr key={c.cle || c.label || i}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--text-strong)' }}>{c.label || c.libelle || c.cle || '—'}</td>
                    <td style={td}>
                      <div>{cellE(eLocal, stLocal)}</div>
                      {pLocal && <div style={{ marginTop: '3px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>{String(pLocal)}</div>}
                    </td>
                    <td style={td}>
                      <div>{cellE(eIa, stIa)}</div>
                      {pIa && <div style={{ marginTop: '3px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>{String(pIa)}</div>}
                    </td>
                    <td style={{ ...td, ...mono, textAlign: 'right', fontWeight: 600, color: ecartE == null ? 'var(--text-3)' : Math.abs(ecartE) >= 2 ? '#b45309' : 'var(--text-2)' }}>
                      {ecartE == null ? '' : `${ecartE > 0 ? '+' : ''}${arrondi(ecartE, 1)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Entrée depuis la configuration : demande un besoin, puis ouvre l'écran.
 * Non-admin : rien à proposer (la route répond 403).
 */
export function DiagnosticScoringSection({ showNotification }) {
  const admin = isAoAdmin(APIService.getCurrentUser());
  const [besoins, setBesoins] = useState(null);
  const [sel, setSel] = useState('');
  const [ouvert, setOuvert] = useState(false);

  useEffect(() => {
    if (!admin) return;
    (async () => {
      try {
        const r = await fetch(`${BASE}/besoins`, { headers: authH() });
        if (!r.ok) { setBesoins([]); return; }
        const j = await r.json().catch(() => ({}));
        const arr = j.data || j.besoins || (Array.isArray(j) ? j : []);
        setBesoins(Array.isArray(arr) ? arr : []);
      } catch { setBesoins([]); }
    })();
  }, [admin]);

  if (!admin) {
    return (
      <div style={{ ...CARTE }}>
        <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: '15px', fontWeight: 800, color: 'var(--text-strong)' }}>Diagnostic du scoring</div>
        <p style={{ marginTop: '6px', marginBottom: 0, fontSize: '12.5px', color: 'var(--text-3)', lineHeight: 1.6 }}>
          Cet outil de comparaison est réservé aux administrateurs de l'organisation.
        </p>
      </div>
    );
  }

  const choisi = (besoins || []).find(b => String(b.id) === String(sel));
  return (
    <div style={{ ...CARTE }}>
      <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: '15px', fontWeight: 800, color: 'var(--text-strong)' }}>Diagnostic du scoring</div>
      <p style={{ marginTop: '6px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
        Compare, sur un besoin donné, le score calculé par l'IA et celui d'un moteur local instantané.
        Sert à décider si le moteur local peut trier ou pré-filtrer les candidats. Les recruteurs, eux,
        ne voient qu'un seul score : celui de l'IA.
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' }}>
        <ErpiaSelect value={sel} onChange={e => setSel(e.target.value)}
          style={{ flex: 1, minWidth: '220px', padding: '8px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px' }}>
          <option value="">{besoins == null ? 'Chargement des besoins…' : 'Choisir un besoin…'}</option>
          {(besoins || []).map(b => (
            <option key={b.id} value={b.id}>{b.poste || b.titre || `Besoin ${b.id}`}{b.client ? ` — ${b.client}` : ''}</option>
          ))}
        </ErpiaSelect>
        <button onClick={() => setOuvert(true)} disabled={!sel} style={{ ...btnGhost, opacity: sel ? 1 : 0.5, cursor: sel ? 'pointer' : 'not-allowed' }}>
          <BI icon="bar-chart-steps" /> Comparer local / IA
        </button>
      </div>
      {ouvert && sel && (
        <ComparaisonScoresModal besoinId={sel} besoinTitre={choisi ? (choisi.poste || choisi.titre || '') : ''}
          showNotification={showNotification} onClose={() => setOuvert(false)} />
      )}
    </div>
  );
}

export default ComparaisonScoresModal;
